import { useEffect, useRef, useState } from "react";
import "./Cam.css";

const polaroidFrame = "/new-polaroid-frame.png";
const photoEffect = "/effect.avif";
const effectOpacity = 0.36;
const photoFilter = "saturate(1.14) contrast(1.08) brightness(1.02)";
const frameSize = {
    width: 1080,
    height: 1350,
};
const photoRect = {
    x: 42,
    y: 46,
    width: 996,
    height: 996,
};
const makeStatus = {
    idle: "",
    loading: "",
    captured: "",
    canceled: "Share canceled.",
    shared: "Shared.",
    saved: "Saved.",
    failed: "Share failed.",
    unsupported: "This browser does not support camera access.",
    permission: "Camera permission is needed to use APT300 Cam.",
};

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const drawCoverImage = (context, source, sourceWidth, sourceHeight, targetRect) => {
    const sourceRatio = sourceWidth / sourceHeight;
    const targetRatio = targetRect.width / targetRect.height;
    let cropWidth = sourceWidth;
    let cropHeight = sourceHeight;
    let cropX = 0;
    let cropY = 0;

    if (sourceRatio > targetRatio) {
        cropWidth = sourceHeight * targetRatio;
        cropX = (sourceWidth - cropWidth) / 2;
    } else {
        cropHeight = sourceWidth / targetRatio;
        cropY = (sourceHeight - cropHeight) / 2;
    }

    context.drawImage(
        source,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        targetRect.x,
        targetRect.y,
        targetRect.width,
        targetRect.height
    );
};


function Cam() {
    const videoRef = useRef(null);
    const previewCanvasRef = useRef(null);
    const canvasRef = useRef(null);
    const frameImageRef = useRef(null);
    const effectImageRef = useRef(null);
    const streamRef = useRef(null);
    const [status, setStatus] = useState(makeStatus.idle);
    const [capturedUrl, setCapturedUrl] = useState("");
    const [capturedBlob, setCapturedBlob] = useState(null);
    const [cameraReady, setCameraReady] = useState(false);
    const [frameReady, setFrameReady] = useState(false);
    const [facingMode, setFacingMode] = useState("environment");
    const [flashSupported, setFlashSupported] = useState(false);
    const [flashOn, setFlashOn] = useState(false);

    const isFrontCamera = facingMode === "user";

    useEffect(() => {
        let isMounted = true;

        const startCamera = async () => {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                setStatus(makeStatus.unsupported);
                return;
            }

            try {
                setCameraReady(false);
                setStatus(makeStatus.loading);

                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: { ideal: facingMode },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    },
                    audio: false,
                });

                if (!isMounted) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }

                streamRef.current = stream;
                videoRef.current.srcObject = stream;
                videoRef.current.muted = true;
                videoRef.current.playsInline = true;
                videoRef.current.setAttribute("playsinline", "");
                videoRef.current.setAttribute("webkit-playsinline", "");
                videoRef.current.removeAttribute("controls");
                await videoRef.current.play();
                const videoTrack = stream.getVideoTracks()[0];
                const capabilities = videoTrack?.getCapabilities?.() || {};
                setFlashSupported(Boolean(capabilities.torch));
                setFlashOn(false);
                setCameraReady(true);
                setStatus(makeStatus.idle);
            } catch (error) {
                setStatus(makeStatus.permission);
                setFlashSupported(false);
                setFlashOn(false);
            }
        };

        startCamera();

        return () => {
            isMounted = false;
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
        };
    }, [facingMode]);

    useEffect(() => {
        return () => {
            if (capturedUrl) {
                URL.revokeObjectURL(capturedUrl);
            }
        };
    }, [capturedUrl]);

    useEffect(() => {
        const frameImage = new Image();
        const effectImage = new Image();
        let loadedImages = 0;

        const markLoaded = () => {
            loadedImages += 1;
            if (loadedImages === 2) {
                frameImageRef.current = frameImage;
                effectImageRef.current = effectImage;
                setFrameReady(true);
            }
        };

        frameImage.onload = markLoaded;
        effectImage.onload = markLoaded;
        frameImage.src = polaroidFrame;
        effectImage.src = photoEffect;
    }, []);

    useEffect(() => {
        let animationFrameId;

        const drawPreview = () => {
            const video = videoRef.current;
            const canvas = previewCanvasRef.current;

            if (video && canvas && video.videoWidth && video.videoHeight) {
                if (canvas.width !== photoRect.width || canvas.height !== photoRect.height) {
                    canvas.width = photoRect.width;
                    canvas.height = photoRect.height;
                }

                const context = canvas.getContext("2d");
                context.clearRect(0, 0, canvas.width, canvas.height);
                context.filter = photoFilter;

                if (isFrontCamera) {
                    context.save();
                    context.translate(canvas.width, 0);
                    context.scale(-1, 1);
                    drawCoverImage(context, video, video.videoWidth, video.videoHeight, {
                        x: 0,
                        y: 0,
                        width: canvas.width,
                        height: canvas.height,
                    });
                    context.restore();
                } else {
                    drawCoverImage(context, video, video.videoWidth, video.videoHeight, {
                        x: 0,
                        y: 0,
                        width: canvas.width,
                        height: canvas.height,
                    });
                }

                context.filter = "none";
            }

            animationFrameId = requestAnimationFrame(drawPreview);
        };

        if (cameraReady && !capturedUrl) {
            drawPreview();
        }

        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
        };
    }, [cameraReady, capturedUrl, isFrontCamera]);

    const setTorch = async (enabled) => {
        const videoTrack = streamRef.current?.getVideoTracks()[0];

        if (!videoTrack) {
            return false;
        }

        try {
            await videoTrack.applyConstraints({
                advanced: [{ torch: enabled }],
            });
            return true;
        } catch (error) {
            setFlashSupported(false);
            setFlashOn(false);
            return false;
        }
    };

    const capturePhoto = async () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;

        if (!video.videoWidth || !video.videoHeight) {
            setStatus("Camera is still getting ready.");
            return;
        }

        if (!frameReady || !frameImageRef.current || !effectImageRef.current) {
            setStatus("Frame is still loading. Try again in a second.");
            return;
        }

        const shouldUseTorch = flashSupported && flashOn;
        if (shouldUseTorch) {
            await setTorch(true);
            await wait(180);
        }

        canvas.width = frameSize.width;
        canvas.height = frameSize.height;
        const context = canvas.getContext("2d");
        context.fillStyle = "#fff";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.filter = photoFilter;
        drawCoverImage(context, video, video.videoWidth, video.videoHeight, photoRect);
        context.filter = "none";
        context.save();
        context.globalAlpha = effectOpacity;
        drawCoverImage(context, effectImageRef.current, effectImageRef.current.naturalWidth, effectImageRef.current.naturalHeight, photoRect);
        context.restore();
        const vignette = context.createRadialGradient(
            photoRect.x + photoRect.width / 2,
            photoRect.y + photoRect.height / 2,
            photoRect.width * 0.22,
            photoRect.x + photoRect.width / 2,
            photoRect.y + photoRect.height / 2,
            photoRect.width * 0.72
        );
        vignette.addColorStop(0, "rgba(0, 0, 0, 0)");
        vignette.addColorStop(0.72, "rgba(0, 0, 0, 0.03)");
        vignette.addColorStop(1, "rgba(0, 0, 0, 0.22)");
        context.fillStyle = vignette;
        context.fillRect(photoRect.x, photoRect.y, photoRect.width, photoRect.height);
        context.drawImage(frameImageRef.current, 0, 0, frameSize.width, frameSize.height);

        if (shouldUseTorch) {
            await setTorch(false);
        }

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
        if (!blob) {
            setStatus("Could not create the photo. Try again.");
            return;
        }

        if (capturedUrl) {
            URL.revokeObjectURL(capturedUrl);
        }

        setCapturedBlob(blob);
        setCapturedUrl(URL.createObjectURL(blob));
        setStatus(makeStatus.captured);
    };

    const downloadPhoto = () => {
        if (!capturedUrl) {
            return false;
        }

        const link = document.createElement("a");
        link.href = capturedUrl;
        link.download = `apt300-${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        return true;
    };

    const sharePhoto = async () => {
        if (!capturedBlob) {
            return;
        }

        const file = new File([capturedBlob], "apt300.jpg", { type: "image/jpeg" });
        const shareData = {
            title: "APT300",
            text: "APT300 live from the event",
            files: [file],
        };

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
                await navigator.share(shareData);
                setStatus(makeStatus.shared);
                return;
            } catch (error) {
                if (error.name === "AbortError") {
                    setStatus(makeStatus.canceled);
                    return;
                }
                const downloaded = downloadPhoto();
                setStatus(downloaded ? makeStatus.saved : makeStatus.failed);
                return;
            }
        }

        const downloaded = downloadPhoto();
        setStatus(downloaded ? makeStatus.saved : makeStatus.failed);
    };

    const retakePhoto = () => {
        setCapturedUrl("");
        setCapturedBlob(null);
        setStatus(makeStatus.idle);
    };

    const toggleCamera = () => {
        setCapturedUrl("");
        setCapturedBlob(null);
        setFlashSupported(false);
        setFlashOn(false);
        setFacingMode((currentFacingMode) => currentFacingMode === "user" ? "environment" : "user");
    };

    const toggleFlash = async () => {
        setFlashOn((currentFlashState) => !currentFlashState);
    };

    return (
        <main className="apt-cam-shell">
            <header className="apt-cam-header">
                <a className="apt-cam-close" href="https://apt300.com" aria-label="Back to apt300.com">
                    <svg viewBox="0 0 64 64" aria-hidden="true">
                        <path d="M18 18 46 46" />
                        <path d="M46 18 18 46" />
                    </svg>
                </a>
                <div className="apt-cam-title-block">
                    <h1>300 Cam</h1>
                    <p>Tag us @apt300_ on IG</p>
                </div>
            </header>

            <section className={`apt-cam-stage ${capturedUrl ? "has-result" : ""}`} aria-label="APT300 camera">
                <video
                    ref={videoRef}
                    className="apt-cam-source-video"
                    autoPlay
                    playsInline
                    muted
                    controls={false}
                    disablePictureInPicture
                    disableRemotePlayback
                />
                <div className="apt-cam-photo-window">
                    <canvas ref={previewCanvasRef} className={`apt-cam-preview-canvas ${cameraReady ? "is-camera-ready" : ""}`} aria-hidden="true" />
                    <img className="apt-cam-effect" src={photoEffect} alt="" aria-hidden="true" />
                    <div className="apt-cam-vignette" aria-hidden="true" />
                </div>
                <img className="apt-cam-frame" src={polaroidFrame} alt="" aria-hidden="true" />
                <canvas ref={canvasRef} className="apt-cam-canvas" aria-hidden="true" />
                {capturedUrl && <img className="apt-cam-result" src={capturedUrl} alt="Captured APT300" />}
            </section>

            <section className="apt-cam-controls" aria-label="Camera controls">
                {status && <p className="apt-cam-status">{status}</p>}
                <div className="apt-cam-actions">
                    {capturedUrl ? (
                        <>
                            <button className="apt-cam-icon-button back" type="button" onClick={retakePhoto} aria-label="Back to camera">
                                <svg viewBox="0 0 64 64" aria-hidden="true">
                                    <path d="M39 14 21 32l18 18" />
                                </svg>
                            </button>
                            <button className="apt-cam-icon-button share" type="button" onClick={sharePhoto} aria-label="Share photo">
                                <svg viewBox="0 0 64 64" aria-hidden="true">
                                    <path d="M32 42V10" />
                                    <path d="M20 22 32 10l12 12" />
                                    <path d="M22 30h-4a7 7 0 0 0-7 7v10a7 7 0 0 0 7 7h28a7 7 0 0 0 7-7V37a7 7 0 0 0-7-7h-4" />
                                </svg>
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="apt-cam-icon-button flip" type="button" onClick={toggleCamera} aria-label={isFrontCamera ? "Use back camera" : "Use front camera"}>
                                <svg viewBox="0 0 64 64" aria-hidden="true">
                                    <path d="M47 25a16 16 0 0 0-28-9l-4 4" />
                                    <path d="M15 10v10h10" />
                                    <path d="M17 39a16 16 0 0 0 28 9l4-4" />
                                    <path d="M49 54V44H39" />
                                </svg>
                            </button>
                            <button className="apt-cam-shutter" type="button" onClick={capturePhoto} disabled={!cameraReady} aria-label="Take photo">
                                <span />
                            </button>
                            {flashSupported && (
                                <button className={`apt-cam-icon-button flash ${flashOn ? "is-on" : ""}`} type="button" onClick={toggleFlash} aria-label={flashOn ? "Disable flash for photo" : "Enable flash for photo"}>
                                    <svg viewBox="0 0 64 64" aria-hidden="true">
                                        <path d="M36 6 18 35h14l-4 23 18-31H32z" />
                                    </svg>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </section>
        </main>
    );
}

export default Cam;
